import {
  BufferAttribute,
  BufferGeometry,
  FrontSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial
} from 'three';
import { decodeAssembly } from '../assembly-proto-decoder';
import { EngineContext } from './engine-context';
import {
  FEAResultInfoV2,
  FEAResultMeshSet,
  ProtoAssembly,
  ProtoPart
} from '../engine.types';
import {
  OPTIMIZED_METALNESS,
  OPTIMIZED_REFLECTIVITY,
  OPTIMIZED_ROUGHNESS,
  OPTIMIZED_CLEARCOAT,
  OPTIMIZED_ENVMAP_INTENSITY,
  FACE_POLYGON_OFFSET
} from '../engine.constants';

export class AssemblyManager {
  constructor(private ctx: EngineContext) {}

  /**
   * Kept `async` for call-site compatibility; the decode itself is synchronous
   * now that there is no schema to fetch.
   *
   * The previous implementation called `protobuf.load()` here — re-fetching and
   * re-parsing the .proto text on *every* assembly (base, organic, parametric)
   * and decoding by reflection into `number[]`, which the geometry path then had
   * to copy into typed arrays. `decodeAssembly` reads the buffer once, straight
   * into `Float32Array`/`Uint32Array`.
   *
   * The old `msg.verify(payload)` call was also a no-op: `verify()` validates a
   * plain JS object against the schema, not an encoded buffer, so passing a
   * Uint8Array always returned null.
   */
  async decodeAssemblyProto(buffer: ArrayBuffer): Promise<ProtoAssembly> {
    return decodeAssembly(buffer);
  }

  async setAssembly(assembly: ArrayBuffer): Promise<void> {
    this.ctx.assembly = await this.decodeAssemblyProto(assembly);
  }

  async setOptimizedAssembly(assembly: ArrayBuffer): Promise<void> {
    this.ctx.assemblyOptimized = await this.processOptimizedAssembly(assembly, 'organic');
  }

  async setOptimizedAssemblyParametric(assembly: ArrayBuffer): Promise<void> {
    this.ctx.assemblyOptimizedParametric = await this.processOptimizedAssembly(assembly, 'prismatic');
  }

  private async processOptimizedAssembly(
    assembly: ArrayBuffer,
    variant: 'organic' | 'prismatic'
  ): Promise<Group> {
    const isOrganic = variant === 'organic';
    const meshName = isOrganic ? 'OptimizedOrganicMesh' : 'OptimizedPrismaticMesh';
    const lineName = isOrganic ? 'OptimizedOrganicMeshLine' : 'OptimizedPrismaticMeshLine';

    const decoded = await this.decodeAssemblyProto(assembly);

    const material = new MeshPhysicalMaterial({
      color: '#A4C7E1',
      side: FrontSide,
      vertexColors: false,
      flatShading: true,
      opacity: 1,
      transparent: false,
      metalness: OPTIMIZED_METALNESS,
      reflectivity: OPTIMIZED_REFLECTIVITY,
      roughness: OPTIMIZED_ROUGHNESS,
      clearcoat: OPTIMIZED_CLEARCOAT,
      envMap: this.ctx.envMap,
      envMapIntensity: OPTIMIZED_ENVMAP_INTENSITY,
      ...FACE_POLYGON_OFFSET
    });

    const wirematerial = this.ctx.createWireLineMaterial();

    const groupPromises = decoded.parts.map(async (obj: ProtoPart) => {
      const group = new Group();
      group.name = obj.name;

      let totalVertexCount = 0;
      let totalIndexCount = 0;
      for (const face of obj.faces) {
        totalVertexCount += face.vertices.length / 3;
        totalIndexCount += face.indices.length;
      }

      const mergedPositions = new Float32Array(totalVertexCount * 3);
      const mergedIndices = new Uint32Array(totalIndexCount);
      let vertexOffset = 0;
      let indexOffset = 0;
      let currentVertexIndex = 0;

      for (const face of obj.faces) {
        const vertices = face.vertices;
        const indices = face.indices;
        mergedPositions.set(vertices, vertexOffset);
        vertexOffset += vertices.length;
        for (let i = 0; i < indices.length; i++) {
          mergedIndices[indexOffset + i] = indices[i] + currentVertexIndex;
        }
        indexOffset += indices.length;
        currentVertexIndex += vertices.length / 3;
      }

      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(mergedPositions, 3));
      geometry.setIndex(new BufferAttribute(mergedIndices, 1));
      geometry.computeVertexNormals();
      geometry.computeBoundingSphere();
      geometry.computeBoundingBox();

      const mesh = new Mesh(geometry, material);
      mesh.name = meshName;
      group.add(mesh);

      const { wiresGroup } = this.ctx.createMergedWireGroup(obj.wires, wirematerial, lineName);
      group.add(wiresGroup);

      return group;
    });

    const groups = await Promise.all(groupPromises);
    const resultGroup = new Group();
    resultGroup.add(...groups);
    return resultGroup;
  }

  setOptimizedInfo(info: FEAResultInfoV2 | FEAResultMeshSet[]): void {
    // Reset any previously-loaded FEA state before (re)populating. Without
    // this, calling `setOptimizedInfo` twice — as the multi-manufacturing
    // case switcher does on every case change — accumulates duplicate
    // loadcase entries and duplicate FEA meshes in `infoGroup`.
    this.resetOptimizedInfoState();

    const basicVertexColorsMaterial = new MeshBasicMaterial({
      vertexColors: true,
      clippingPlanes: this.ctx.clippingPlanes
    });
    if (this.ctx.session.project.flowVersion === 2) {
      const infoV2 = info as FEAResultInfoV2;
      this.ctx.optimizeInfoFlowVersion2 = infoV2;
      (this.ctx.assemblyOptimizedInfo as FEAResultMeshSet[]).push(...infoV2.organic);

      for (const key in infoV2) {
        if (infoV2[key]) {
          infoV2[key]!.forEach((item: FEAResultMeshSet) => {
            ['t', 'x', 'y', 'z', 's'].forEach((type) => {
              const mesh = item[type] as Mesh;
              mesh.name = `lc_${item.id}`;
              mesh.visible = false;
              mesh.material = basicVertexColorsMaterial;
              mesh.userData['fea_type'] = key;
              this.ctx.infoGroup.add(mesh);
            });
          });
        }
      }
    } else {
      const infoArr = info as FEAResultMeshSet[];
      (this.ctx.assemblyOptimizedInfo as FEAResultMeshSet[]).push(...infoArr);
      infoArr.forEach((item: FEAResultMeshSet) => {
        ['t', 'x', 'y', 'z', 's'].forEach((type) => {
          const mesh = item[type] as Mesh;
          mesh.name = `lc_${item.id}_${type}`;
          mesh.visible = false;
          mesh.material = basicVertexColorsMaterial;
          this.ctx.infoGroup.add(mesh);
        });
      });
    }

    this.ctx.currentLoadCase = (this.ctx.assemblyOptimizedInfo as FEAResultMeshSet[])[0].id;
  }

  /**
   * Tear down the previous setOptimizedInfo run's FEA state so a subsequent
   * call starts from a clean slate. Called from `setOptimizedInfo` to make
   * it idempotent across multi-manufacturing case swaps.
   *
   * Disposes:
   *  - every `infoGroup` child's geometry + material (FEA meshes, one per
   *    loadcase × displacement channel)
   *  - `assemblyOptimizedInfo` (the loadcase list the Results UI reads)
   *  - `optimizeInfoFlowVersion2` (organic/prismatic view buckets)
   *  - `currentLoadCase` (the selected LC pointer)
   */
  private resetOptimizedInfoState(): void {
    const infoGroup = this.ctx.infoGroup;
    if (infoGroup) {
      for (const child of [...(infoGroup.children ?? [])]) {
        (child as unknown as { traverse?: (cb: (obj: unknown) => void) => void })
          .traverse?.((obj: unknown) => {
            const mesh = obj as { geometry?: { dispose?: () => void }; material?: unknown };
            mesh.geometry?.dispose?.();
            const mat = mesh.material;
            if (Array.isArray(mat)) {
              for (const m of mat) (m as { dispose?: () => void }).dispose?.();
            } else if (mat) {
              (mat as { dispose?: () => void }).dispose?.();
            }
          });
        infoGroup.remove(child);
      }
    }

    // Empty the array in place so any consumer holding a live reference
    // (e.g. ResultTypeSetupService iterating via `engine.assemblyOptimizedInfo`)
    // sees the cleared state.
    if (Array.isArray(this.ctx.assemblyOptimizedInfo)) {
      (this.ctx.assemblyOptimizedInfo as FEAResultMeshSet[]).length = 0;
    }

    this.ctx.optimizeInfoFlowVersion2 = { organic: [], prismatic: [] } as unknown as FEAResultInfoV2;
    this.ctx.currentLoadCase = null;
  }
}
